import { createSlice } from "@reduxjs/toolkit";
import { ReplayState } from "../types/replay";

const initialState: ReplayState = {
  v2Merged: {},
  processedSessionData: null,
  availableSeasons: [],
  availableEvents: [],
  availableSessions: [],
  selectedSeason: null,
  selectedEvent: null,
  selectedSession: null,
  loadingSeasons: false,
  loadingEvents: false,
  loadingSessions: false,
  loadingSessionEventData: false,
  selectedSessionEvents: [],
  replayState: {
    connected: false,
    params: {
      sessionId: null,
      timeSkip: 0,
      skipPreGreenLive: true,
      skipNonRealTimeLive: true,
    },
    playing: false,
    loadingReplay: false,
    replayStartTime: null,
    processedSessionData: {
      attackStatusByDriverLapLoop: {},
      attackActivationsByDriver: {},
      lapStartTimes: {},
      flagStartTimes: [],
      cautionLaps: [],
      lapStartTimesByDriver: {},
      driverNamesByNumber: {},
      sessionName: "",
    },
    hilightedDriver: null,
  },
};

export const aksSlice = createSlice({
  name: "replay",
  initialState,
  reducers: {
    setSessionEventData: (state, action) => {
      const { v2Merged, processedSessionData } = action.payload;
      state.v2Merged = v2Merged;
      state.processedSessionData = processedSessionData;
    },
    setAvailableReplaySeasons: (state, action) => {
      const seasons = action.payload;
      state.availableSeasons = seasons;
      state.loadingSeasons = false;
    },
    setReplayEventsForSeason: (state, action) => {
      const events = action.payload;
      state.availableEvents = events;
      state.loadingEvents = false;
    },
    setReplaySessionsForEvent: (state, action) => {
      const sessions = action.payload;
      state.availableSessions = sessions;
      state.loadingSessions = false;
    },
    setSelectedSeason: (state, action) => {
      state.selectedSeason = action.payload;
      state.selectedEvent = null;
      state.selectedSession = null;
      state.availableEvents = [];
      state.availableSessions = [];
      state.v2Merged = {};
      state.loadingEvents = true;
    },
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload;
      state.selectedSession = null;
      state.availableSessions = [];
      state.v2Merged = {};
      state.loadingSessions = true;
    },
    setSelectedSession: (state, action) => {
      state.selectedSession = action.payload;
      state.v2Merged = {};
      state.processedSessionData = null;
      if (!state.replayState.playing) {
        state.replayState.params.sessionId = action.payload.sessionId;
      }
    },
    toggleSelectedSessionIsReal: (state) => {
      if (state.selectedSession) {
        state.selectedSession.isReal = !state.selectedSession.isReal;
        // Also update the session in availableSessions so the table reflects the change
        const sessionIndex = state.availableSessions.findIndex(
          (s) => s.sessionId === state.selectedSession?.sessionId,
        );
        if (sessionIndex !== -1) {
          state.availableSessions[sessionIndex].isReal =
            state.selectedSession.isReal;
        }
      }
    },
    setLoadingSeasons: (state, action) => {
      state.loadingSeasons = action.payload;
    },
    setLoadingEvents: (state, action) => {
      state.loadingEvents = action.payload;
    },
    setLoadingSessions: (state, action) => {
      state.loadingSessions = action.payload;
    },
    setLoadingSessionEventData: (state, action) => {
      state.loadingSessionEventData = action.payload;
    },
    setReplayState: (state, action) => {
      state.replayState = action.payload;
    },
    updateReplayState: (state, action) => {
      const replayStateUpdate = action.payload;

      if (replayStateUpdate.connected !== null) {
        state.replayState.connected = replayStateUpdate.connected;
      }

      if (replayStateUpdate.params !== null) {
        if (replayStateUpdate.params.sessionId !== null) {
          state.replayState.params.sessionId =
            replayStateUpdate.params.sessionId;
        }
        if (replayStateUpdate.params.timeSkip !== null) {
          state.replayState.params.timeSkip = replayStateUpdate.params.timeSkip;
        }
        if (replayStateUpdate.params.skipPreGreenLive !== null) {
          state.replayState.params.skipPreGreenLive =
            replayStateUpdate.params.skipPreGreenLive;
        }
        if (replayStateUpdate.params.skipNonRealTimeLive != null) {
          state.replayState.params.skipNonRealTimeLive =
            replayStateUpdate.params.skipNonRealTimeLive;
        }
      }

      if (replayStateUpdate.playing !== null) {
        state.replayState.playing = replayStateUpdate.playing;
        const selectedSessionId = state.selectedSession?.sessionId || null;
        if (!replayStateUpdate.playing && selectedSessionId !== null) {
          state.replayState.params.sessionId = selectedSessionId;
        }
      }

      if (replayStateUpdate.loadingReplay !== null) {
        state.replayState.loadingReplay = replayStateUpdate.loadingReplay;
      }

      if (replayStateUpdate.replayStartTime !== null) {
        state.replayState.replayStartTime = replayStateUpdate.replayStartTime;
      }

      if (replayStateUpdate.processedSessionData !== null) {
        state.replayState.processedSessionData =
          replayStateUpdate.processedSessionData;
      }
    },
    setReplayStateParamsTimeSkip: (state, action) => {
      state.replayState.params.timeSkip = action.payload;
    },
    setReplayStateParamsSkipPreGreenLive: (state, action) => {
      state.replayState.params.skipPreGreenLive = action.payload;
    },
    setReplayStateParamsSkipNonRealTimeLive: (state, action) => {
      state.replayState.params.skipNonRealTimeLive = action.payload;
    },
    setReplayStateHilightedDriver: (state, action) => {
      state.replayState.hilightedDriver = action.payload;
    },
  },
});

export const {
  setSessionEventData,
  setAvailableReplaySeasons,
  setReplayEventsForSeason,
  setReplaySessionsForEvent,
  setSelectedSeason,
  setSelectedEvent,
  setSelectedSession,
  toggleSelectedSessionIsReal,
  setLoadingSeasons,
  setLoadingEvents,
  setLoadingSessions,
  setLoadingSessionEventData,
  setReplayState,
  updateReplayState,
  setReplayStateParamsTimeSkip,
  setReplayStateParamsSkipPreGreenLive,
  setReplayStateParamsSkipNonRealTimeLive,
  setReplayStateHilightedDriver,
} = aksSlice.actions;
export default aksSlice.reducer;
